<template>
  <div class="summary-table-container">
    <!-- Filter Controls -->
    <div class="box mb-4">
      <div class="columns is-vcentered">
        <div class="column is-3">
          <div class="field">
            <label class="label is-size-7">Filter Period</label>
            <div class="control">
              <div class="select is-fullwidth is-small">
                <select v-model="filterPeriod" @change="fetchData">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label is-size-7">Start Date</label>
            <div class="control">
              <input 
                class="input is-small" 
                type="date" 
                v-model="startDate"
                @change="fetchData"
              >
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label is-size-7">End Date</label>
            <div class="control">
              <input 
                class="input is-small" 
                type="date" 
                v-model="endDate"
                @change="fetchData"
              >
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label is-size-7">&nbsp;</label>
            <div class="control">
              <button 
                class="button is-small" 
                style="background-color: #0a3954; color: white;"
                @click="fetchData"
                :class="{ 'is-loading': isLoading }"
              >
                <span class="icon is-small">
                  <i class="fas fa-sync-alt"></i>
                </span>
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="has-text-centered p-6">
      <span class="icon is-large">
        <i class="fas fa-spinner fa-spin"></i>
      </span>
      <p class="mt-3">Loading invoice processing statistics...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="notification is-danger is-light">
      <span class="icon">
        <i class="fas fa-exclamation-triangle"></i>
      </span>
      <span>{{ error }}</span>
    </div>

    <!-- Summary Table -->
    <div v-else-if="statisticsData && statisticsData.statistics" class="box">
      <h3 class="title is-6 mb-4">Invoice Processing Statistics Summary</h3>
      
      <!-- Responsive table wrapper -->
      <div class="table-container">
        <table class="table is-fullwidth is-striped is-hoverable is-size-7">
          <thead>
            <tr style="background-color: #0a1821; color: white;">
              <th class="has-text-white">Period</th>
              <!-- Source System Headers (Dynamic) -->
              <th v-for="system in sourceSystemHeaders" 
                  :key="`${system}-count`" 
                  class="has-text-white has-text-centered">
                {{ system }}<br>
                <small class="is-size-7">Invoice Count</small>
              </th>
              <th v-for="system in sourceSystemHeaders" 
                  :key="`${system}-amount`" 
                  class="has-text-white has-text-centered">
                {{ system }}<br>
                <small class="is-size-7">Invoice Amount</small>
              </th>
              <!-- Status Headers -->
              <th class="has-text-white has-text-centered">VALIDATED</th>
              <th class="has-text-white has-text-centered">SUBMISSION FAILED</th>
              <th class="has-text-white has-text-centered">OTHERS</th>
              <th class="has-text-white has-text-centered">TOTAL INV</th>
              <th class="has-text-white has-text-centered">Successful Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(period, index) in statisticsData.statistics" :key="index">
              <!-- Period Date -->
              <td class="has-text-weight-semibold">{{ period.deduction_date }}</td>
              
              <!-- Source System Counts -->
              <td v-for="system in sourceSystemHeaders" 
                  :key="`${system}-count-${index}`" 
                  class="has-text-centered">
                {{ formatNumber(getSourceSystemCount(period, system)) }}
              </td>
              
              <!-- Source System Amounts -->
              <td v-for="system in sourceSystemHeaders" 
                  :key="`${system}-amount-${index}`" 
                  class="has-text-centered">
                {{ formatCurrency(getSourceSystemAmount(period, system)) }}
              </td>
              
              <!-- Status Counts -->
              <td class="has-text-centered" style="color: #0a3954; font-weight: 600;">
                {{ formatNumber(getStatusCount(period, 'VALIDATED')) }}
              </td>
              <td class="has-text-centered" style="color: #dc3545; font-weight: 600;">
                {{ formatNumber(getStatusCount(period, 'SUBMISSION FAILED')) }}
              </td>
              <td class="has-text-centered">
                {{ formatNumber(getStatusCount(period, 'OTHERS')) }}
              </td>
              <td class="has-text-centered has-text-weight-bold">
                {{ formatNumber(period.status_summary.total_invoices_sent) }}
              </td>
              <td class="has-text-centered has-text-weight-bold">
                {{ period.status_summary.success_rate_percentage }}%
              </td>
            </tr>
          </tbody>
          <!-- Summary Footer -->
          <tfoot v-if="statisticsData.summary">
            <tr style="background-color: #f8f9fa; font-weight: bold;">
              <td class="has-text-weight-bold">TOTAL</td>
              <!-- Source System Totals -->
              <td v-for="system in sourceSystemHeaders" 
                  :key="`total-${system}-count`" 
                  class="has-text-centered has-text-weight-bold">
                {{ formatNumber(getSummarySystemCount(system)) }}
              </td>
              <td v-for="system in sourceSystemHeaders" 
                  :key="`total-${system}-amount`" 
                  class="has-text-centered has-text-weight-bold">
                {{ formatCurrency(getSummarySystemAmount(system)) }}
              </td>
              <td class="has-text-centered has-text-weight-bold" style="color: #0a3954;">-</td>
              <td class="has-text-centered has-text-weight-bold" style="color: #dc3545;">-</td>
              <td class="has-text-centered has-text-weight-bold">-</td>
              <td class="has-text-centered has-text-weight-bold">
                {{ formatNumber(statisticsData.summary.total_invoices_all_periods) }}
              </td>
              <td class="has-text-centered has-text-weight-bold">
                {{ statisticsData.summary.overall_success_rate }}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Summary Cards -->
      <div class="columns is-multiline mt-4">
        <div class="column is-3">
          <div class="card" style="background-color: #0a1821; color: white;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ statisticsData.summary.total_periods }}</p>
              <p class="subtitle is-6 has-text-white">Total Periods</p>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="card" style="background-color: #0a3954; color: white;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ formatNumber(statisticsData.summary.total_invoices_all_periods) }}</p>
              <p class="subtitle is-6 has-text-white">Total Invoices</p>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="card" style="background-color: #082B3F; color: white;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ statisticsData.summary.overall_success_rate }}%</p>
              <p class="subtitle is-6 has-text-white">Overall Success Rate</p>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="card" style="background-color: #364b5c; color: white;">
            <div class="card-content has-text-centered">
              <p class="title is-4 has-text-white">{{ sourceSystemHeaders.length }}</p>
              <p class="subtitle is-6 has-text-white">Source Systems</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div v-else class="has-text-centered p-6">
      <span class="icon is-large has-text-grey">
        <i class="fas fa-table"></i>
      </span>
      <p class="mt-3 has-text-grey">No data available for the selected period.</p>
    </div>
  </div>
</template>

<script>
import { getInvoiceProcessingStats } from '@/api/invoiceStatsApi';

export default {
  name: 'SummaryTableView',
  data() {
    return {
      isLoading: false,
      error: null,
      statisticsData: null,
      filterPeriod: 'weekly',
      startDate: '',
      endDate: '',
      sourceSystemHeaders: []
    }
  },
  async mounted() {
    this.setDefaultDates();
    await this.fetchData();
  },
  methods: {
    setDefaultDates() {
      const today = new Date();
      const sixtyDaysAgo = new Date(today.getTime() - (60 * 24 * 60 * 60 * 1000));
      
      this.endDate = today.toISOString().split('T')[0];
      this.startDate = sixtyDaysAgo.toISOString().split('T')[0];
    },
    
    async fetchData() {
      this.isLoading = true;
      this.error = null;
      
      try {
        const params = {
          filter: this.filterPeriod,
          start_date: this.startDate,
          end_date: this.endDate
        };
        
        const response = await getInvoiceProcessingStats(params);
        this.statisticsData = response;
        this.extractSourceSystemHeaders();
        
      } catch (error) {
        console.error('Error fetching statistics:', error);
        this.error = 'Failed to load invoice processing statistics. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },
    
    extractSourceSystemHeaders() {
      if (!this.statisticsData || !this.statisticsData.statistics.length) return;
      
      const systemsSet = new Set();
      this.statisticsData.statistics.forEach(period => {
        if (period.source_system_aggregation) {
          period.source_system_aggregation.forEach(system => {
            systemsSet.add(system.source_system);
          });
        }
      });
      
      this.sourceSystemHeaders = Array.from(systemsSet).sort();
    },
    
    getSourceSystemCount(period, systemName) {
      if (!period.source_system_aggregation) return 0;
      const system = period.source_system_aggregation.find(s => s.source_system === systemName);
      return system ? system.invoice_count : 0;
    },
    
    getSourceSystemAmount(period, systemName) {
      if (!period.source_system_aggregation) return 0;
      const system = period.source_system_aggregation.find(s => s.source_system === systemName);
      return system ? system.total_value : 0;
    },
    
    getStatusCount(period, statusName) {
      if (!period.status_aggregation) return 0;
      const status = period.status_aggregation.find(s => s.status === statusName);
      return status ? status.invoice_count : 0;
    },
    
    getSummarySystemCount(systemName) {
      if (!this.statisticsData.summary || !this.statisticsData.summary.source_system_totals) return 0;
      const systemTotal = this.statisticsData.summary.source_system_totals[systemName];
      return systemTotal ? systemTotal.total_count : 0;
    },
    
    getSummarySystemAmount(systemName) {
      if (!this.statisticsData.summary || !this.statisticsData.summary.source_system_totals) return 0;
      const systemTotal = this.statisticsData.summary.source_system_totals[systemName];
      return systemTotal ? systemTotal.total_value : 0;
    },
    
    formatNumber(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat().format(value);
    },
    
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'MYR',
        minimumFractionDigits: 2
      }).format(value);
    }
  }
}
</script>

<style scoped>
.summary-table-container {
  padding: 0;
}

.table-container {
  overflow-x: auto;
}

.table th {
  vertical-align: middle;
  text-align: center;
  padding: 0.75rem 0.5rem;
  border-color: #0a3954;
}

.table td {
  padding: 0.75rem 0.5rem;
  vertical-align: middle;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .table {
    font-size: 0.75rem;
  }
  
  .table th,
  .table td {
    padding: 0.5rem 0.25rem;
  }
  
  .columns {
    margin: 0;
  }
  
  .column {
    padding: 0.5rem;
  }
}

/* Make sure long numbers don't break layout */
.table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

.table th {
  white-space: nowrap;
}
</style>
